import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Divider,
  Dropdown,
  Form,
  Grid,
  Header,
  Input,
  Message,
  Modal
} from 'semantic-ui-react';
import CustomDatePicker from '../../../../components/CustomDatePicker';
import AppMaskedInput from '../../../../components/MaskedInput';
import { FieldLabel } from '../../../../styles';
import { DATE_INPUT_MASK } from '../../../../services/masks';
import { get } from '../../../../services/storage';
import { UserRolesEnum } from '../../../../enums/user-roles.enum';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../../../services/notification';
import {
  INVOICE_MANUAL_BILLET_FILE_NAME,
  removeFireInsuranceInvoiceBillet,
  replaceFireInsuranceInvoiceBillet,
  updateFireInsuranceInvoice
} from '../../../../services/fire-insurance-invoices';
import { getFiles } from '../../../../services/file';
import {
  InvoicesStatusList,
  translateInvoiceStatus
} from '../../utils/AlfaSeguros';
import ConfirmInvoiceChangesModal from './ConfirmInvoiceChangesModal';
import moment from 'moment-timezone';
import { AdminEditInvoiceModalData, FieldDiff, InvoiceFormState } from './types';
import {
  buildUpdatePayload,
  computeFieldDiffs,
  mapInvoiceToFormState,
  maskMoneyInput,
  summarizeDiffs,
  validateForm
} from './utils';
import {
  BilletBox,
  BilletPending,
  MoneyInputWrapper,
  ReadOnlyGrid
} from './styles';
import { Info } from '../../../../components/Info';

type ManualBillet = {
  link: string;
  createdAt?: string;
};

type AdminEditInvoiceModalProps = {
  open: boolean;
  close: () => void;
  data?: AdminEditInvoiceModalData;
  onSuccess: () => void;
};

const AdminEditInvoiceModal = ({
  open,
  close,
  data,
  onSuccess
}: AdminEditInvoiceModalProps) => {
  const userRole = get('role');
  const isBrokerAdmin = userRole === UserRolesEnum.brokerAdmin;

  const [form, setForm] = useState<InvoiceFormState | null>(null);
  const [original, setOriginal] = useState<InvoiceFormState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [billetFile, setBilletFile] = useState<File | null>(null);
  const [removeManualBillet, setRemoveManualBillet] = useState(false);
  const [manualBillet, setManualBillet] = useState<ManualBillet | null>(null);
  const billetInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open || !data) {
      return;
    }
    setBilletFile(null);
    setRemoveManualBillet(false);
    setManualBillet(null);
    getFiles({
      tableName: 'fire_insurance_invoices',
      registerId: data.id,
      name: INVOICE_MANUAL_BILLET_FILE_NAME
    })
      .then(response => {
        const file = response?.urls?.[0];
        setManualBillet(
          file?.link ? { link: file.link, createdAt: file.createdAt } : null
        );
      })
      .catch(() => setManualBillet(null));

    const mapped = mapInvoiceToFormState(data);
    setForm(mapped);
    setOriginal(mapped);
    setConfirmOpen(false);
  }, [open, data]);

  const diffs = useMemo(() => {
    if (!form || !original) {
      return [];
    }
    const fieldDiffs: FieldDiff[] = computeFieldDiffs(
      original,
      form,
      translateInvoiceStatus
    );

    if (billetFile || removeManualBillet) {
      const currentLabel = manualBillet
        ? `Boleto enviado manualmente${
            manualBillet.createdAt
              ? ` em ${moment(manualBillet.createdAt).format('DD/MM/YYYY')}`
              : ''
          }`
        : 'Boleto da seguradora';
      fieldDiffs.push({
        key: 'billet',
        label: 'Boleto',
        oldValue: currentLabel,
        newValue: billetFile ? billetFile.name : 'Boleto da seguradora'
      });
    }

    return fieldDiffs;
  }, [form, original, billetFile, removeManualBillet, manualBillet]);

  const hasChanges = diffs.length > 0;

  const updateField = <K extends keyof InvoiceFormState>(
    key: K,
    value: InvoiceFormState[K]
  ) => {
    setForm(prev => {
      if (!prev) {
        return prev;
      }
      const next = { ...prev, [key]: value };
      if (key === 'status' && value !== 'CLOSED') {
        next.paymentDate = null;
      }
      return next;
    });
  };

  const handleSelectBilletFile = (file: File | null) => {
    if (billetInputRef.current) {
      billetInputRef.current.value = '';
    }
    if (!file) {
      return;
    }
    if (file.type !== 'application/pdf') {
      warningNotification('Opss...', 'O boleto deve ser um arquivo PDF.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      warningNotification('Opss...', 'O boleto deve ter no máximo 10MB.');
      return;
    }
    setBilletFile(file);
    setRemoveManualBillet(false);
  };

  const handleClose = () => {
    if (isLoading) {
      return;
    }
    setConfirmOpen(false);
    close();
  };

  const handleSaveClick = () => {
    if (!form || !original || !isBrokerAdmin) {
      return;
    }

    const validationError = validateForm(form);
    if (validationError) {
      warningNotification('Opss...', validationError);
      return;
    }

    if (!hasChanges) {
      warningNotification('Opss...', 'Nenhuma alteração para salvar.');
      return;
    }

    setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    if (!form || !original) {
      return;
    }

    const payload = buildUpdatePayload(form, original);
    let fieldsSaved = false;

    setIsLoading(true);
    try {
      if (Object.keys(payload).length > 0) {
        await updateFireInsuranceInvoice(form.invoiceId, payload);
      }
      fieldsSaved = true;

      if (billetFile) {
        await replaceFireInsuranceInvoiceBillet(form.invoiceId, billetFile);
      } else if (removeManualBillet) {
        await removeFireInsuranceInvoiceBillet(form.invoiceId);
      }

      successNotification(
        'Sucesso',
        `Fatura atualizada: ${summarizeDiffs(diffs)}.`
      );
      setConfirmOpen(false);
      onSuccess();
      close();
    } catch (error) {
      const message =
        (error as any)?.message || 'Não foi possível atualizar a fatura.';
      if (fieldsSaved) {
        dangerNotification(
          'Atenção',
          `Os dados da fatura foram salvos, mas o boleto não foi atualizado: ${message}`
        );
        setConfirmOpen(false);
        onSuccess();
        close();
      } else {
        dangerNotification('Erro', message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        size="small"
        closeOnDimmerClick={!isLoading}
        closeOnEscape={!isLoading}
      >
        <Header icon="edit" content="Editar Fatura" />
        <Modal.Content>
          {!isBrokerAdmin && (
            <Message negative>
              Apenas administradores do corretor podem editar faturas por esta
              modal.
            </Message>
          )}

          <ReadOnlyGrid>
            <Grid>
              <Grid.Row>
                <Info name="Imobiliária" value={form?.estateName || ''} width={8} />
                <Info name="Seguradora" value={form?.insurerName || ''} width={8} />
              </Grid.Row>
              <Grid.Row>
                <Info
                  name="Código do contrato"
                  value={form?.contractCode || '—'}
                  width={5}
                />
                <Info
                  name="Código de emissão"
                  value={form?.emissionCode || '—'}
                  width={5}
                />
                <Info
                  name="Nº contribuição"
                  value={form?.contributionNumber || '—'}
                  width={6}
                />
              </Grid.Row>
            </Grid>
          </ReadOnlyGrid>

          <Form>
            <Grid>
              <Grid.Row>
                <Grid.Column width={8}>
                  <FieldLabel required>Valor total</FieldLabel>
                  <MoneyInputWrapper>
                    <Input
                      fluid
                      disabled={!isBrokerAdmin || isLoading}
                      value={form?.amount || ''}
                      onChange={(_, { value }) =>
                        updateField('amount', maskMoneyInput(String(value)))
                      }
                    />
                  </MoneyInputWrapper>
                </Grid.Column>
                <Grid.Column width={8}>
                  <FieldLabel required>Status</FieldLabel>
                  <Dropdown
                    fluid
                    selection
                    disabled={!isBrokerAdmin || isLoading}
                    options={InvoicesStatusList}
                    value={form?.status || ''}
                    onChange={(_, { value }) =>
                      updateField('status', String(value || ''))
                    }
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={8}>
                  <FieldLabel required>Data de vencimento</FieldLabel>
                  <CustomDatePicker
                    selected={form?.dueDate}
                    disabled={!isBrokerAdmin || isLoading}
                    onChange={(date: Date | null) => updateField('dueDate', date)}
                    dateFormat="dd/MM/yyyy"
                    customInput={<AppMaskedInput mask={DATE_INPUT_MASK} />}
                  />
                </Grid.Column>
                <Grid.Column width={8}>
                  <FieldLabel required={form?.status === 'CLOSED'}>
                    Data de pagamento
                  </FieldLabel>
                  <CustomDatePicker
                    selected={form?.paymentDate}
                    disabled={
                      !isBrokerAdmin || isLoading || form?.status !== 'CLOSED'
                    }
                    onChange={(date: Date | null) =>
                      updateField('paymentDate', date)
                    }
                    dateFormat="dd/MM/yyyy"
                    customInput={<AppMaskedInput mask={DATE_INPUT_MASK} />}
                    isClearable
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>

          <Divider />
          <FieldLabel>Boleto</FieldLabel>
          <BilletBox>
            <div>
              {manualBillet ? (
                <>
                  Boleto enviado manualmente
                  {manualBillet.createdAt
                    ? ` em ${moment(manualBillet.createdAt).format(
                        'DD/MM/YYYY [às] HH:mm'
                      )}`
                    : ''}
                  {' · '}
                  <a href={manualBillet.link} target="_blank" rel="noreferrer">
                    Ver boleto
                  </a>
                </>
              ) : (
                'Boleto original da seguradora'
              )}
            </div>
            {(billetFile || removeManualBillet) && (
              <BilletPending>
                {billetFile
                  ? `Novo boleto selecionado: ${billetFile.name}`
                  : 'O boleto manual será removido; volta a valer o da seguradora.'}
                {' — será aplicado ao salvar.'}
              </BilletPending>
            )}
          </BilletBox>
          <input
            ref={billetInputRef}
            type="file"
            accept="application/pdf"
            style={{ display: 'none' }}
            onChange={event =>
              handleSelectBilletFile(event.target.files?.[0] || null)
            }
          />
          <Button
            basic
            size="small"
            icon="file pdf outline"
            content={
              billetFile ? 'Trocar PDF selecionado' : 'Substituir boleto (PDF)'
            }
            disabled={!isBrokerAdmin || isLoading}
            onClick={() => billetInputRef.current?.click()}
          />
          {billetFile && (
            <Button
              basic
              size="small"
              icon="undo"
              content="Descartar seleção"
              disabled={isLoading}
              onClick={() => setBilletFile(null)}
            />
          )}
          {manualBillet && !billetFile && (
            <Button
              basic
              size="small"
              color={removeManualBillet ? undefined : 'red'}
              icon={removeManualBillet ? 'undo' : 'trash'}
              content={
                removeManualBillet
                  ? 'Desfazer remoção'
                  : 'Remover boleto manual'
              }
              disabled={!isBrokerAdmin || isLoading}
              onClick={() => setRemoveManualBillet(prev => !prev)}
            />
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button disabled={isLoading} onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            color="blue"
            loading={isLoading}
            disabled={isLoading || !isBrokerAdmin || !hasChanges}
            onClick={handleSaveClick}
          >
            Salvar alterações
          </Button>
        </Modal.Actions>
      </Modal>

      <ConfirmInvoiceChangesModal
        open={confirmOpen}
        diffs={diffs}
        isLoading={isLoading}
        onBack={() => setConfirmOpen(false)}
        onConfirm={handleConfirmSave}
      />
    </>
  );
};

export default AdminEditInvoiceModal;
export type { AdminEditInvoiceModalData };
